// TEAMS Website - useScrollReveal Hook
// Custom hook for scroll-triggered animations

import { useEffect, useRef, useState } from 'react';

// Intersection Observer options
const defaultOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0.1
};

/**
 * Custom hook for triggering animations when elements come into view
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Margin around the root
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @param {number} options.delay - Delay before revealing element (ms)
 * @returns {Array} [ref, isVisible]
 */
export const useScrollReveal = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    delay = 0
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [hasRevealed, setHasRevealed] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Skip if already revealed and triggerOnce is true
    if (triggerOnce && hasRevealed) return;

    const observerOptions = {
      root: null,
      rootMargin,
      threshold
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add delay if specified
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              if (triggerOnce) {
                setHasRevealed(true);
              }
            }, delay);
          } else {
            setIsVisible(true);
            if (triggerOnce) {
              setHasRevealed(true);
            }
          }

          // Unobserve after revealing if triggerOnce is true
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          // Hide element when it goes out of view if not triggerOnce
          setIsVisible(false);
        }
      });
    }, observerOptions);

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce, delay, hasRevealed]);

  return [elementRef, isVisible];
};

/**
 * Hook for staggered animations on multiple elements
 * @param {number} count - Number of elements to animate
 * @param {Object} options - Animation options
 * @param {number} options.staggerDelay - Delay between each element (ms)
 * @param {number} options.threshold - Visibility threshold
 * @returns {Array} Array of refs and visibility states
 */
export const useScrollRevealStagger = (count, options = {}) => {
  const { staggerDelay = 100, threshold = 0.1 } = options;
  const [visibleStates, setVisibleStates] = useState(new Array(count).fill(false));
  const refs = useRef(new Array(count).fill(null));
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate elements with stagger
            refs.current.forEach((ref, index) => {
              if (ref) {
                setTimeout(() => {
                  setVisibleStates(prev => {
                    const newStates = [...prev];
                    newStates[index] = true;
                    return newStates;
                  });
                }, index * staggerDelay);
              }
            });

            observer.unobserve(container);
          }
        });
      },
      { threshold }
    );

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, [staggerDelay, threshold]);

  // Create refs for each element
  const elementRefs = refs.current.map((_, index) => ({
    ref: (el) => {
      refs.current[index] = el;
    },
    isVisible: visibleStates[index]
  }));

  return [containerRef, elementRefs];
};


export default useScrollReveal;
